<template>
  <div class="relative flex items-center mb-3">
    <NotebookPen class="pointer-events-none h-5 w-5 mr-2 text-gray-400" aria-hidden="true" />
    <span class="dark:text-white">{{ step.name }}</span>
    <div class="absolute top-0 right-0 flex flex-wrap gap-2 px-2 pb-2 justify-end">
      <div class="relative flex w-full items-center whitespace-nowrap rounded-full hover:cursor-pointer group">
        <X
          class="text-gray-500 group-hover:bg-gray-100 dark:group-hover:bg-dark-screen-background h-6 w-6 flex-shrink-0 rounded-full"
          @click="$emit('cancel')" />
      </div>
    </div>
  </div>
  <div
    class="px-2 min-h-[250px] max-h-[500px] h-[500px] overflow-y-auto relative w-full dark:bg-dark-box-background"
    :class="[fetched_all_notes && notes.length == 0 ? '' : 'overflow-y-auto']">
    <div class="flex justify-between items-center">
      <div class="flex items-center truncate overflow-ellipsis">
        <div
          v-if="is_add_new_note || is_edit_note"
          class="mr-1"
          @click="
            () => {
              is_add_new_note = false
              is_edit_note = false
              note = { title: null, description: null }
            }
          ">
          <ChevronLeft
            class="cursor-pointer h-5 w-5"
            :class="[{ 'rotate-180': $store.state.portfolio.ui_options?.language == 'ar' }]"
            aria-hidden="true" />
        </div>
      </div>
      <button type="button" class="primary-btn w-fit" @click="createUpdateNote()">
        <span class="text-[14px] not-italic font-normal font-sans leading-5">{{
          is_add_new_note || is_edit_note ? $t("Save") : $t("Add new note")
        }}</span>
      </button>
    </div>

    <div v-if="!is_add_new_note && !is_edit_note" class="h-[calc(100%-42px)]">
      <div v-if="!fetched_all_notes && !is_add_new_note && !is_edit_note" class="text-common-grey">
        {{ $t("Loading...") }}
      </div>
      <div
        v-if="fetched_all_notes && notes.length == 0 && !is_add_new_note && !is_edit_note"
        class="w-full h-full -mt-6 flex items-center justify-center text-[14px] not-italic font-normal font-sans leading-5">
        {{ $t("You have no notes added") }}
      </div>
      <div
        v-if="fetched_all_notes && !is_add_new_note && !is_edit_note"
        class="relative w-full h-[calcl(100%-66px)] mb-2 mt-2"
        :class="[{ 'pointer-events-none': loading }]">
        <div v-for="note in notes" :key="note.id" class="flex flex-col border rounded-lg mb-2">
          <div class="pl-2 pr-4 py-2">
            <div
              class="h-[20px] max-h-[20px] min-h-[20px] truncate overflow-ellipsis text-[14px] not-italic font-medium font-sans leading-normal">
              {{ note.title }}
            </div>
            <TextEditor
              :combined-values-listner="note.description"
              class="-my-2 h-[38px] max-h-[38px] min-h-[38px] truncate overflow-ellipsis relative"
              input-classes="-ml-3"
              :transparent-bg="true"
              :disabled="true"
              :no-border="true"
              :value="$utils.extractValue(note.description, 'N/A', $route)"
              :name="`description`"
              label="" />
            <div class="flex justify-between items-center">
              <div class="flex items-center">
                <div class="bg-white rounded-l-lg cursor-pointer" @click="editNote(note)">
                  <FilePenLine v-if="!editing_disabled" class="relative w-5 h-5 opacity-30" />
                </div>
                <Trash2
                  v-if="!is_confirmation_delete_open && !editing_disabled"
                  class="text-common-grey hover:text-red-500 ml-1 cursor-pointer w-5 h-5"
                  @click.prevent="
                    () => {
                      note_to_be_deleted = note
                      is_confirmation_delete_open = true
                    }
                  " />
              </div>
              <div class="flex flex-col items-end text-[12px] not-italic font-normal font-sans leading-normal text-common-grey">
                <span>{{ $utils.getUserById(note.created_by, "full_name") }}</span>
                <span>{{ toDate(note.last_edited) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex flex-col relative w-full h-[calc(100%-46px)] mt-1">
      <div class="mb-1">
        <TextInput
          :value="note.title"
          label=""
          :show-label="false"
          :classes="'w-full'"
          input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
          name="name"
          type="text"
          :placeholder="$t('Title')"
          @update:modelValue="
            (e) => {
              note.title = e
            }
          " />
      </div>
      <div class="w-full relative rounded-md">
        <TextEditor
          :value="note.description"
          :show-label="false"
          :no-border="true"
          toolbar="essential"
          name="note"
          :label="$t('Note')"
          theme="snow"
          input-classes="w-full rounded-b-md"
          :placeholder="$t('Description')"
          @changed="
            (e) => {
              note.description = formatLink(e)
            }
          " />
      </div>
    </div>

    <!-- Delete Note -->
    <Modal
      v-model:is-open="is_confirmation_delete_open"
      max-width="max-w-[600px] mx-5"
      :use-toast="true"
      :default-success-msg="$t('Note deleted successfully')">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Note") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Once you delete note, you can't restore it.") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="confirmDeleteNote(showIsDone)">
            {{ $t("Delete Note") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import TextEditor from "@/components/shared/TextEditor.vue"
import dataService from "@/services/data.service"
import { NotebookPen, FilePenLine, X, Trash2, ChevronLeft } from "lucide-vue-next"
import TextInput from "@/components/shared/TextInput.vue"
import Modal from "@/components/shared/Modal.vue"

export default {
  components: {
    Modal,
    TextInput,
    FilePenLine,
    TextEditor,
    NotebookPen,
    X,
    Trash2,
    ChevronLeft
  },
  inject: ["canUserEditLayout"],
  props: {
    step: {
      type: Object,
      required: true
    }
  },
  emits: ["cancel"],
  data() {
    return {
      is_add_new_note: false,
      is_edit_note: false,
      is_confirmation_delete_open: false,
      note: { id: null, title: null, description: null },
      note_to_be_deleted: null,
      notes: [],
      fetched_all_notes: false,
      loading: false
    }
  },

  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },

  created() {
    this.init()
  },
  activated() {
    this.init()
  },

  methods: {
    init() {
      this.notes = this.step.notes.results
      this.fetched_all_notes = true
    },

    addNewNote() {
      let index = this.notes.findIndex((note) => note.id == this.note?.id)
      if (index != -1) {
        //Update existing note
        dataService
          .updateUserNote(this.$store.state.auth.user.id, this.note.id, {
            title: this.note.title,
            description: this.note.description
          })
          .then((res) => {
            this.notes[index] = res.data
            this.note = { title: null, description: null }
          })
      } else {
        //Create new note
        dataService
          .createUserNote(this.$store.state.auth.user.id, {
            title: this.note.title,
            description: this.note.description
          })
          .then((res) => {
            dataService.addNoteToMilestoneStep(this.step.id, { notes_ids: [res.data.id] }).then(() => {
              this.notes.unshift(res.data)
              this.note = { title: null, description: null }
            })
          })
      }
    },

    editNote(note) {
      this.is_edit_note = true
      this.note = note
    },

    confirmDeleteNote(showIsDone) {
      dataService
        .removeNoteFromMilestoneStep(this.step.id, {
          notes_ids: [this.note_to_be_deleted.id]
        })
        .then(() => {
          let index = this.notes.findIndex((note) => note.id == this.note_to_be_deleted.id)
          this.notes.splice(index, 1)
          showIsDone()
        })
    },

    createUpdateNote() {
      if (this.is_add_new_note && !this.is_edit_note) {
        this.is_add_new_note = false
        this.addNewNote()
      } else if (!this.is_add_new_note && !this.is_edit_note) {
        this.is_add_new_note = true
      } else if (this.is_edit_note) {
        this.is_edit_note = false
        this.addNewNote()
      }
    },

    toDate(date_string) {
      let date = new Date(date_string)
      const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" }
      let localStringDate = date.toLocaleDateString(undefined, options)
      return localStringDate.substr(localStringDate.indexOf(",") + 1)
    },

    formatLink(str) {
      return str.replace(/href="(?!http)/, 'href="http://')
    }
  }
}
</script>
